import React from "react";
import * as palette from "@govlaunch/palette";
import { Button, LoadingIndicator } from "@govlaunch/core";
import HighlightsInputField from "~/components/form/HighlightsInputField";
import NewsworthyField from "~/components/form/NewsworthyField";
import Section from "~/components/form/sections/Section";
import SectionBody from "~/components/form/sections/SectionBody";
import SectionHeader from "~/components/form/sections/SectionHeader";
import { EmployeesNumberField } from "~/components/vendor/form/fields/EmployeesNumberField";
import SectionHeaderMakers from "~/components/form/sections/SectionHeaderMakers";
import SetProfileCover from "~/lib/mutations/SetProfileCover";
import Cover, { PLACEHOLDER_COVER, makeImaginaryUrls } from "~/components/cover/Cover";
import getOr from "lodash/fp/getOr";
import { IVendorFieldsFragment } from "~/lib/queries/fragments/__generated__/VendorFields.generated";
import StickyFooter from "~/components/form/StickyFooter";
import SocialMediaFields from "~/components/form/SocialMediaFields";
import TextField from "~/components/form/TextField";
import TextareaField from "~/components/form/TextareaField";
import UrlField from "~/components/form/UrlField";
import ResourcesField from "~/components/form/ResourcesField";
import VendorLogoField from "~/components/vendor/form/fields/VendorLogoField";
import { FormSpy } from "react-final-form";
import NumberField from "~/components/form/NumberField";
import { useIntl, FormattedMessage } from "react-intl";

interface IVendorFormProps {
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  submitting: boolean;
  submitButtonText?: string;
  initialVendorName?: string;
  vendor: IVendorFieldsFragment;
}

const VendorForm: React.FunctionComponent<IVendorFormProps> = ({ vendor, onSubmit, submitting, submitButtonText }) => {
  const intl = useIntl();

  return (
    <form onSubmit={onSubmit}>
      <div
        css={{
          position: "relative",
        }}
      >
        <SetProfileCover entityType="Company" targetId={vendor._id}>
          {({ onSetProfileCover, onSetRepositionedCover, onRemoveCover }) => (
            <Cover
              alt="Cover"
              height={250}
              originalSource={getOr(null, "cover.original", vendor)}
              repositionedSource={getOr(null, "cover.repositioned", vendor)}
              placeholderSource={PLACEHOLDER_COVER}
              onReposition={(originalSource, cropState) => {
                const repositionedSource = makeImaginaryUrls(originalSource, cropState);
                onSetRepositionedCover(originalSource, repositionedSource.cropped, cropState);
              }}
              onChange={onSetProfileCover}
              onRemove={onRemoveCover}
              readOnly={!vendor.viewerIsMember}
              containerProps={{
                style: {
                  borderRadius: 5,
                  overflow: "hidden",
                },
              }}
            />
          )}
        </SetProfileCover>

        <div
          css={{
            position: "absolute",
            top: "50%",
            marginTop: -30,
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <FormSpy subscription={{ values: true }}>
            {({ values }) => <VendorLogoField vendorName={values.name} />}
          </FormSpy>
        </div>
      </div>

      <div
        css={{
          "& > div": {
            marginTop: 20,
          },
        }}
      >
        <Section>
          <SectionHeader>
            <FormattedMessage defaultMessage="The Basics" id="5L4CQD" />
          </SectionHeader>

          <SectionBody>
            <TextField
              fieldName="name"
              label={intl.formatMessage({
                defaultMessage: "Name",
                id: "HAlOn1",
              })}
              placeholder={intl.formatMessage({
                defaultMessage: "Type vendor name",
                id: "VOkTGq",
              })}
              aria-label={intl.formatMessage({
                defaultMessage: "Vendor name",
                id: "7GHATk",
              })}
            />
            <TextField
              fieldName="billingName"
              label={intl.formatMessage({
                defaultMessage: "Billing Name",
                id: "9CSOR+",
              })}
              placeholder={intl.formatMessage({
                defaultMessage: "Type vendor billing name",
                id: "BPybV6",
              })}
              aria-label={intl.formatMessage({
                defaultMessage: "Billing Name",
                id: "9CSOR+",
              })}
            />
            <TextareaField
              fieldName="description"
              label={intl.formatMessage({
                defaultMessage: "Description",
                id: "Q8Qw5B",
              })}
              placeholder={intl.formatMessage({
                defaultMessage: "Type vendor description",
                id: "uB2OE7",
              })}
              characterLimit={240}
              aria-label={intl.formatMessage({
                defaultMessage: "Vendor description",
                id: "7FxVks",
              })}
            />
            <NumberField
              fieldName="yearFounded"
              label={intl.formatMessage({
                defaultMessage: "Year Founded",
                id: "ccKFl8",
              })}
              placeholder={intl.formatMessage({
                defaultMessage: "Type Year",
                id: "HxsQY1",
              })}
            />
            <UrlField
              fieldName="site"
              label={intl.formatMessage({
                defaultMessage: "Site",
                id: "pYwSd7",
              })}
              placeholder={intl.formatMessage({
                defaultMessage: "Type vendor site",
                id: "Fmid4X",
              })}
              aria-label={intl.formatMessage({
                defaultMessage: "Vendor site",
                id: "TkZjxS",
              })}
            />
          </SectionBody>
        </Section>

        <Section>
          <SectionHeaderMakers
            target={vendor.isDisruptor ? "DISRUPTOR" : "MAKERS"}
            disruptorTooltip={intl.formatMessage({
              defaultMessage: `This will display because you're a Disruptor`,
              id: "ZL6pcN",
            })}
            makersTooltip={intl.formatMessage({
              defaultMessage: "Your account must be Makers for this to appear",
              id: "lvHUQf",
            })}
          >
            <FormattedMessage defaultMessage="More Info" id="vWe94i" />
          </SectionHeaderMakers>

          <SectionBody>
            <EmployeesNumberField />
            <TextField
              fieldName="headquarters"
              label={intl.formatMessage({ defaultMessage: "Headquarters", id: "kFQl9u" })}
              placeholder={intl.formatMessage({
                defaultMessage: "Type vendor headquarters",
                id: "3Dlk3F",
              })}
              characterLimit={240}
              aria-label={intl.formatMessage({
                defaultMessage: "Vendor headquarters",
                id: "ga/Gdu",
              })}
            />
            <TextField
              fieldName="slogan"
              label={intl.formatMessage({ defaultMessage: "Slogan", id: "mgRuC7" })}
              placeholder={intl.formatMessage({
                defaultMessage: "Type your vendor slogan",
                id: "a9g+eH",
              })}
              characterLimit={180}
              aria-label={intl.formatMessage({
                defaultMessage: "Vendor slogan",
                id: "u43UNX",
              })}
            />
            <TextField
              fieldName="contact"
              label={intl.formatMessage({ defaultMessage: "Contact", id: "zFegDD" })}
              placeholder={intl.formatMessage({
                defaultMessage: "Type a URL or email for contact",
                id: "sf7Cnb",
              })}
              aria-label={intl.formatMessage({
                defaultMessage: "Vendor contact URL or email",
                id: "qf4MsP",
              })}
            />
          </SectionBody>
        </Section>

        <Section>
          <SectionHeaderMakers
            target={vendor.isDisruptor ? "DISRUPTOR" : "MAKERS"}
            disruptorTooltip={intl.formatMessage({
              defaultMessage: `This will display because you're a Disruptor`,
              id: "ZL6pcN",
            })}
            makersTooltip={intl.formatMessage({
              defaultMessage: "Your account must be Makers for this to appear",
              id: "lvHUQf",
            })}
          >
            <FormattedMessage defaultMessage="Social Media" id="5bGX59" />
          </SectionHeaderMakers>

          <SectionBody>
            <SocialMediaFields />
          </SectionBody>
        </Section>

        <Section>
          <SectionHeaderMakers
            target={vendor.isDisruptor ? "DISRUPTOR" : "MAKERS"}
            disruptorTooltip={intl.formatMessage({
              defaultMessage: `This will display because you're a Disruptor`,
              id: "ZL6pcN",
            })}
            makersTooltip={intl.formatMessage({
              defaultMessage: "Your account must be Makers for this to appear",
              id: "lvHUQf",
            })}
          >
            <FormattedMessage defaultMessage="Resources" id="c/KktL" />
          </SectionHeaderMakers>

          <SectionBody>
            <ResourcesField fieldName="resources" />
          </SectionBody>
        </Section>

        <Section>
          <SectionHeaderMakers
            target={vendor.isDisruptor ? "DISRUPTOR" : "MAKERS"}
            disruptorTooltip={intl.formatMessage({
              defaultMessage: `This will display because you're a Disruptor`,
              id: "ZL6pcN",
            })}
            makersTooltip={intl.formatMessage({
              defaultMessage: "Your account must be Makers for this to appear",
              id: "lvHUQf",
            })}
          >
            <FormattedMessage defaultMessage="Highlights" id="KGmQjH" />
          </SectionHeaderMakers>
          <HighlightsInputField fieldName="highlights" />
        </Section>

        <Section>
          <SectionHeaderMakers
            target={vendor.isDisruptor ? "DISRUPTOR" : "MAKERS"}
            disruptorTooltip={intl.formatMessage({
              defaultMessage: `This will display because you're a Disruptor`,
              id: "ZL6pcN",
            })}
            makersTooltip={intl.formatMessage({
              defaultMessage: "Your account must be Makers for this to appear",
              id: "lvHUQf",
            })}
          >
            <FormattedMessage defaultMessage="Newsworthy" id="JhItYg" />
          </SectionHeaderMakers>
          <NewsworthyField fieldName="newsworthy" />
        </Section>
      </div>

      <StickyFooter css={{ marginTop: 16 }}>
        <div
          css={{
            padding: 16,
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gridColumnGap: 20,
          }}
        >
          <Button onClick={() => history.back()} color={palette.red} theme="secondary" size="large" type="button">
            <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
          </Button>

          <Button
            size="large"
            color={palette.greenSuccess}
            textColor="#fff"
            type="submit"
            disabled={submitting}
            css={{
              ":disabled": {
                background: "rgba(0, 0, 0, 0.2)",
              },
            }}
          >
            {submitButtonText || intl.formatMessage({ defaultMessage: "Save", id: "jvo0vs" })}
            {submitting && (
              <div css={{ marginLeft: 10 }}>
                <LoadingIndicator />
              </div>
            )}
          </Button>
        </div>
      </StickyFooter>
    </form>
  );
};

export default VendorForm;
